import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api';
import { useAuthStore, type User } from '@/stores/auth';
import { GlassCard } from '@/components/ui/glass-card';
import { getOAuthErrorMessage } from '@/lib/authErrors';
import { redirectDesktopOAuthIfNeeded } from '@/lib/oauthCallback';

export const authDiscordCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/discord/callback',
  component: DiscordAuthCallbackPage,
});

// 後方互換性のため
export const Route = authDiscordCallbackRoute;

function DiscordAuthCallbackPage() {
  const navigate = useNavigate();
  const { setUser, setInitialized, setLoading } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const isProcessing = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution (React StrictMode)
      if (isProcessing.current) return;
      isProcessing.current = true;

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const errorParam = params.get('error');

      if (errorParam) {
        setError(getOAuthErrorMessage(errorParam));
        return;
      }

      if (!code || !state) {
        setError('Missing authorization code or state');
        return;
      }

      try {
        const redirected = await redirectDesktopOAuthIfNeeded(code, state);
        if (redirected) {
          return;
        }

        // Mark as initialized immediately to prevent initializeAuth from running
        setInitialized(true);
        setLoading(true);

        // Call backend callback endpoint with code and state as query params
        const callbackParams = new URLSearchParams({ code, state });
        const response = await apiClient.get<{ user: User }>(
          `/auth/discord/callback?${callbackParams.toString()}`
        );
        setUser(response.user);
        setLoading(false);

        navigate({ to: '/gallery' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setLoading(false);
      }
    };

    processCallback();
  }, [navigate, setUser, setInitialized, setLoading]);

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background p-4"
        data-testid="discord-auth-callback-error"
      >
        <GlassCard className="w-full max-w-md p-8 text-center" intensity="medium">
          <h1 className="mb-4 text-2xl font-bold text-white">Discord Authentication Failed</h1>
          <p className="mb-6 text-white/80">{error}</p>
          <a
            href="/login"
            className="inline-block rounded-lg bg-white px-6 py-2 font-medium text-foreground hover:bg-muted"
          >
            Back to Login
          </a>
        </GlassCard>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background"
      data-testid="discord-auth-callback-loading"
    >
      <div className="text-center">
        <div
          className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"
          role="status"
          aria-label="Processing Discord authentication"
        />
        <p className="text-lg font-medium text-white">Authenticating with Discord...</p>
      </div>
    </div>
  );
}
